function safeParse(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * Normalizes conv_filter_params into { mandatory, optional }.
 *
 * Accepts either:
 * - API message format: { mandatory: [...], optional: [...] }
 * - Raw conv_filter JSON (report items): { mandatory_params, optional_params, sql, ... }
 *
 * @param {object|null} params
 * @returns {{ mandatory: Array, optional: Array } | null}
 */
export function parseConvFilterParams(params) {
  if (!params) return null;

  // Already in normalized format (from API message_json / ConvFilterParams.build)
  if (params.mandatory) return params;

  // Raw conv_filter JSON from report items
  if (params.mandatory_params) {
    const mandatory = safeParse(params.mandatory_params) || [];
    const optional = (safeParse(params.optional_params) || []).map(p => ({
      attribute: p.attribute,
      position: p.position,
      value_required: /<OPTIONAL_ATTRIBUTE_\d+>/.test(p.sql_fragment || ''),
    }));
    return { mandatory, optional };
  }

  return null;
}

/**
 * Opens a new browser tab with a filtered conversation view.
 *
 * @param {import('vue-router').Router} router
 * @param {string} paramKey - 'dam' for messages, 'dari' for report items
 * @param {object} entity - Must have `.id`
 * @param {object} payload - { filterParams, rowValues, optionalPosition }
 */
export function openConversationFilter(
  router,
  paramKey,
  entity,
  { filterParams, rowValues, optionalPosition }
) {
  const queryParams = new URLSearchParams();
  queryParams.set(paramKey, entity.id);

  const { mandatory = [], optional = [] } = filterParams;

  // Always include mandatory params (with values)
  mandatory.forEach(({ position }) => {
    const key = `col${position}`;
    const value = rowValues[key] ?? Object.values(rowValues)[position - 1];
    if (value !== undefined && value !== null) {
      queryParams.set(key, value);
    }
  });

  // Include the clicked optional param (if any)
  if (optionalPosition != null) {
    const optParam = optional.find(p => p.position === optionalPosition);
    if (optParam) {
      const key = `col${optParam.position}`;
      if (optParam.value_required) {
        const value =
          rowValues[key] ?? Object.values(rowValues)[optParam.position - 1];
        if (value !== undefined && value !== null) {
          queryParams.set(key, value);
        }
      } else {
        // Static fragment — just signal which column was clicked
        queryParams.set(key, '');
      }
    }
  }

  const resolved = router.resolve({ name: 'home' });
  const url = `${resolved.href}?${queryParams.toString()}`;
  window.open(url, '_blank');
}
